import { Col, Drawer, Row, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { ReactComponent as IconChevron } from '../../icons/icon-chevron.svg';
import { RootStoresContext } from '../../stores/RootStore';
import { cleanString } from '../../util/renderHelpers';
import Content from '../Content/Content';
import ErrorPage from '../Error/ErrorPage';
import Loading from '../Loading/Loading';

import { toJS } from 'mobx';
import { ReactComponent as IconCloseMenu } from '../../icons/icon-close.svg';
import { PhasesProps } from '../../stores/PhasesStore';
import FlowChartCanvas from './FlowChartCanvas';
import PrevNextNav from './PrevNextNav';

const ChartPhases: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const history = useHistory();
  const { phaseName, step } = useParams();
  const location = useLocation();
  const currentSelected = step
    ? location.pathname.slice(0, location.pathname.indexOf(`/${step}`))
    : location.pathname;

  const scrollToTop = () => window.scrollTo(0, 0);

  const selectHandler = (value: string) => {
    history.push(value);
    scrollToTop();
  };

  const displayChartPhases = rootStore.phasesStore.phasesArray
    ? rootStore.phasesStore.phasesArray.map(chartPhase => (
        <li className="chart__phases-item" key={chartPhase.phaseId}>
          <Link
            to={chartPhase.phasePath}
            onClick={scrollToTop}
            className={`chart__phases-link${
              phaseName && chartPhase.phasePath.indexOf(phaseName) !== -1 ? ' current' : ''
            }`}
          >
            <Col className="chart__phases-icon-wrapper">
              <span
                className={`chart__phases-icon ${chartPhase.phaseColor}`}
                style={{ backgroundImage: `url(${chartPhase.phaseIcon})` }}
              />
            </Col>
            <Col className="chart__phases-title-wrapper">
              <h2 className="chart__phases-title">{cleanString(chartPhase.phaseName)}</h2>
            </Col>
          </Link>
        </li>
      ))
    : null;

  const displayMobileChartPhases = rootStore.phasesStore.phasesArray
    ? rootStore.phasesStore.phasesArray.map(chartPhase => (
        <Select.Option
          key={`pahse-option-${chartPhase.phaseId}`}
          title={cleanString(chartPhase.phaseName)}
          className="phase-option"
          value={chartPhase.phasePath}
        >
          <div className="pahse-option__wrapper">
            <span
              className={`chart__phases-icon ${chartPhase.phaseColor}`}
              style={{ backgroundImage: `url(${chartPhase.phaseIcon})` }}
            />
            <span className="pahse-option__name">{cleanString(chartPhase.phaseName)}</span>
          </div>
        </Select.Option>
      ))
    : null;

  return rootStore.phasesStore.phasesArray ? (
    <nav className="chart__phases">
      {rootStore.appStore.isMobile && displayMobileChartPhases ? (
        <Select
          defaultValue={currentSelected}
          value={currentSelected}
          onChange={selectHandler}
          suffixIcon={
            <span className="phases-chevron">
              <IconChevron />
            </span>
          }
          dropdownClassName="phases-dropdown"
          className="phases-select"
        >
          {displayMobileChartPhases}
        </Select>
      ) : (
        <ul className="chart__phases-list">{displayChartPhases && displayChartPhases}</ul>
      )}
    </nav>
  ) : null;
});

type ArrowPhasesProps = {
  children?: never;
  phases: PhasesProps[] | null;
};

const ArrowPhases: React.FC<ArrowPhasesProps> = ({ phases }) => {
  const history = useHistory();
  const location = useLocation();
  const { step } = useParams();

  const currentPhasePath = step
    ? location.pathname.slice(0, location.pathname.indexOf(`/${step}`))
    : location.pathname;

  const currentIndexPhase = phases
    ? phases.findIndex(phase => phase.phasePath === currentPhasePath)
    : null;
  const prevPath =
    phases &&
    phases[(currentIndexPhase as number) - 1] &&
    phases[(currentIndexPhase as number) - 1].phasePath;
  const nextPath =
    phases &&
    phases[(currentIndexPhase as number) + 1] &&
    phases[(currentIndexPhase as number) + 1].phasePath;

  const onClickPrev = () => prevPath && history.push(prevPath);
  const onClickNext = () => nextPath && history.push(nextPath);

  return currentIndexPhase !== null ? (
    <div className="phases__control">
      {prevPath && (
        <button
          aria-label="prevprevious phase"
          onClick={onClickPrev}
          className="prev__phase control"
        >
          <IconChevron />
        </button>
      )}
      {nextPath && (
        <button aria-label="next phase" onClick={onClickNext} className="next__phase control">
          <IconChevron />
        </button>
      )}
    </div>
  ) : null;
};

const ChartContentDrawer: React.FC = observer(() => {
  const location = useLocation();
  const history = useHistory();
  const { step } = useParams();
  const rootStore = useContext(RootStoresContext);
  const [dragH, setDragH] = useState(205);

  const openDrawer = step !== undefined;
  const currentPhasePath = step
    ? location.pathname.slice(0, location.pathname.indexOf(`/${step}`))
    : location.pathname;
  // console.log(openDrawer);
  rootStore.appStore.isMobile && openDrawer
    ? (document.body.style.overflowY = 'hidden')
    : (document.body.style.overflowY = '');

  const closeDrawerHandler = () => {
    history.push(currentPhasePath);
  };

  const handleMouseDown = () => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseUp = (e: MouseEvent) => {
    setDragH(e.clientY - 12);
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    // ANTI PATTERN, never use document.querySelector instead ref. But in this case Antd Drawer component doesn't allow pass ref.
    const drawer: HTMLDivElement | null = document.querySelector('.step-content__drawer');
    if (drawer) {
      drawer.style.height = `calc(100% - ${e.clientY - 12}px)`;
    }
  };

  const handleTouchStart = () => {
    document.body.style.overflowY = 'hidden';
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
  };

  const handleTouchMove = (e: TouchEvent) => {
    // ANTI PATTERN, never use document.querySelector instead ref. But in this case Antd Drawer component doesn't allow pass ref.
    const drawer: HTMLDivElement | null = document.querySelector('.step-content__drawer');
    if (drawer) {
      drawer.style.height = `calc(100% - ${e.touches[0].clientY - 12}px)`;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    document.body.style.overflowY = '';
    setDragH(e.changedTouches[0].clientY - 12);
    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', handleTouchEnd);
  };

  return (
    <Drawer
      placement="bottom"
      closable={false}
      onClose={closeDrawerHandler}
      visible={openDrawer}
      height="top"
      className="step-content__drawer main"
      style={rootStore.appStore.isMobile ? {} : { height: `calc(100% - ${dragH}px)` }}
      destroyOnClose={true}
      maskClosable={false}
      mask={false}
    >
      <div
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className="grab-bar"
        title="Click and drag to increase or decrease height"
      >
        <span className="grab" />
      </div>
      <button
        aria-label="Close Step Drawer"
        className="step-content__close"
        onClick={closeDrawerHandler}
      >
        <IconCloseMenu />
      </button>
      <div className="page__wrapper drawer standard">
        <Row className="fixed" type="flex">
          <Content col={{ xs: 24 }} type="step" />
        </Row>
      </div>
      <PrevNextNav />
    </Drawer>
  );
});

const ChartContent: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);

  const fetchSteps = useCallback(() => {
    rootStore.stepsStore.getCurrentSteps(
      `${process.env.REACT_APP_API || ''}${process.env.REACT_APP_BASE_PATH || ''}${process.env
        .REACT_APP_STEPS_API || ''}?phase=${rootStore.phasesStore.currentPhaseId}`
    );
  }, [rootStore.stepsStore, rootStore.phasesStore.currentPhaseId]);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  return (
    <div className="chart__content">
      {rootStore.stepsStore.isPending && (
        <Col className="hin-loading" xs={{ span: 24 }}>
          <Loading />
        </Col>
      )}
      {rootStore.stepsStore.currentSteps && (
        <>
          <ArrowPhases phases={toJS(rootStore.phasesStore.phasesArray)} />
          <FlowChartCanvas
            steps={toJS(rootStore.stepsStore.currentSteps)}
            isMobile={rootStore.appStore.isMobile as boolean}
          />
          <ChartContentDrawer />
        </>
      )}
      {rootStore.stepsStore.error && (
        <ErrorPage
          errorContent={`${rootStore.stepsStore.error.name ? ' - ' : ''}${
            rootStore.stepsStore.error.message
          }`}
        />
      )}
    </div>
  );
});

const Chart: React.FC = observer(() => {
  const rootStore = useContext(RootStoresContext);
  const { phaseName } = useParams();

  const fetchPhases = useCallback(() => {
    rootStore.phasesStore.getPhases(
      `${process.env.REACT_APP_API || ''}${process.env.REACT_APP_BASE_PATH || ''}${process.env
        .REACT_APP_PHASES_API || ''}`,
      phaseName
    );
  }, [rootStore.phasesStore, phaseName]);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  return (
    <>
      {rootStore.phasesStore.isPending && (
        <Row className="fixed chart__loading-page" type="flex">
          <Col className="hin-loading" xs={{ span: 24 }}>
            <Loading />
          </Col>
        </Row>
      )}
      {rootStore.phasesStore.currentPhaseId && (
        <div className={`chart__wrapper`}>
          <ChartPhases />
          <ChartContent />
        </div>
      )}
      {(rootStore.phasesStore.error || rootStore.phasesStore.currentPhaseId === null) &&
        !rootStore.phasesStore.isPending && (
          <Row className="fixed chart__error-page" type="flex">
            <ErrorPage errorContent={rootStore.siteStore.getPageNotFound as string} />
          </Row>
        )}
    </>
  );
});

export default Chart;
